/**
 * Meetings API Service
 * Connects to NestJS backend /meetings endpoints
 */

import apiClient from './client';

// ── Types ──────────────────────────────────────────────────────────────────

export enum MeetingType {
  VIDEO = 'video',
  PHONE = 'phone',
  IN_PERSON = 'in_person',
}

export enum MeetingStatus {
  SCHEDULED = 'scheduled',
  CONFIRMED = 'confirmed',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  NO_SHOW = 'no_show',
}

export interface Meeting {
  id: string;
  title: string;
  description?: string;
  type: MeetingType;
  status: MeetingStatus;
  scheduledAt: string;
  duration?: string;
  dealId?: string;
  participants: string[];
  location?: string;
  meetingUrl?: string;
  notes?: string;
  cancellationReason?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMeetingDto {
  title: string;
  description?: string;
  type: MeetingType;
  scheduledAt: string;
  duration?: string;
  dealId?: string;
  participants: string[];
  location?: string;
  meetingUrl?: string;
  notes?: string;
}

export interface UpdateMeetingDto {
  title?: string;
  description?: string;
  type?: MeetingType;
  status?: MeetingStatus;
  scheduledAt?: string;
  duration?: string;
  participants?: string[];
  location?: string;
  meetingUrl?: string;
  notes?: string;
  cancellationReason?: string;
}

export interface FilterMeetingDto {
  dealId?: string;
  status?: MeetingStatus;
  type?: MeetingType;
  fromDate?: string;
  toDate?: string;
}

// ── API ────────────────────────────────────────────────────────────────────

export const meetingsApi = {
  /** Créer une réunion */
  async create(data: CreateMeetingDto): Promise<Meeting> {
    const response = await apiClient.post<Meeting>('/meetings', data);
    return response.data;
  },

  /** Lister les réunions avec filtres optionnels */
  async list(filters?: FilterMeetingDto): Promise<Meeting[]> {
    const response = await apiClient.get<Meeting[]>('/meetings', { params: filters });
    return response.data;
  },

  /** Récupérer une réunion par ID */
  async getById(id: string): Promise<Meeting> {
    const response = await apiClient.get<Meeting>(`/meetings/${id}`);
    return response.data;
  },

  /** Mettre à jour une réunion */
  async update(id: string, data: UpdateMeetingDto): Promise<Meeting> {
    const response = await apiClient.patch<Meeting>(`/meetings/${id}`, data);
    return response.data;
  },

  /** Annuler une réunion */
  async cancel(id: string, reason?: string): Promise<Meeting> {
    const response = await apiClient.post<Meeting>(`/meetings/${id}/cancel`, { reason });
    return response.data;
  },
};
